import {Avatar, Dropdown, Image, Input, message, Tabs} from 'antd'
import {FiMoreHorizontal} from 'react-icons/fi'
import getAvatar from 'helpers/getAvatar'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useMemo, useState} from 'react'
import {renderIf} from 'views/Shared'
import classNames from "classnames";
import {renderIfElse} from "../../../Shared";
import EmptyHolder from "../../../../components/EmptyHolder";
import {ItemInformation} from 'components/common/ItemInformation'
import {EstimatedDobTag} from 'components/common/EstimatedDobTag'
import getLabelOfGender from "../../../../helpers/getLabelOfGender";
import {formatDate, renderFromToDate} from "../../../../helpers/datetimeHelpers";
import {FaUserGraduate, FaUserTie} from "react-icons/fa6";
import {IdentifierTypes} from "../../appFieldTypes";
import {Link} from "react-router-dom";
import getLinkToDetail from "../../../../helpers/getLinkToDetail";
import {BsCalendar2Date} from "react-icons/bs";
import {PiBookOpenText, PiGenderIntersexBold} from "react-icons/pi";
import getAliasName from "../../../../helpers/getAliasName";
import {LayoutContext} from "../../../../components/layouts/Default/LayoutContext";
import {GoDotFill} from "react-icons/go";
import {renderEmptyHolderMember} from "../../CatholicHousehold/functions/renderEmptyHolderMember";
import {convertStringToDate} from "../../../../helpers/dateUtils";
import {HiOutlineLink, HiOutlineShieldCheck, HiViewfinderCircle} from "react-icons/hi2";
import {RiShieldCheckFill} from "react-icons/ri";
import {IoCopyOutline, IoSearchOutline} from "react-icons/io5";
import {IDENTITY_STATUS, IdentityVerificationSection} from "./components/StudentIdentitySection";
import copyToClipboard from "copy-to-clipboard";

const ItemInformationStudent = ({label, value, primary = false}) => {
    const t = useTranslate()
    return (
        <div className="flex flex-col">
            <span className="text-xs text-color-400 tracking-wider">
                {t(label)}
            </span>
            <span className={classNames("text-sm break-words tracking-wide", primary === true ? 'font-bold text-color-000' : 'font-semibold text-gray-800')}>
                {value}
            </span>
        </div>
    )
}

// Metadata tab (key + i18n key của label) - dùng để build menu overflow "..." khớp với thứ tự
// tab trong <Tabs items={...}> bên dưới. Đổi tab thì cập nhật cả 2 chỗ.
const TAB_META = [
    {key: '0', labelKey: 'basic info'},
    {key: '1', labelKey: 'identity verification'},
    {key: '2', labelKey: 'enrolled courses'},
    {key: '3', labelKey: 'subject results'},
    {key: '4', labelKey: 'activity history'},
]

// Card dùng chung cho 3 khối của tab "Thông tin cơ bản" - tiêu đề uppercase + viền dưới, khớp mockup
// (THÔNG TIN CÁ NHÂN / GHI CHÚ & LỊCH SỬ DỮ LIỆU / THÔNG TIN LIÊN HỆ).
const InfoCard = ({title, children, className}) => {
    const t = useTranslate()
    return (
        <div className={classNames("flex flex-col gap-2 p-3 border border-color-100 rounded-2xl", className)}>
            <span className="font-bold text-sm text-color-000 uppercase tracking-wide pb-2 border-b border-color-50">
                {t(title)}
            </span>
            {children}
        </div>
    )
}

// Số liệu tóm tắt (khóa học/môn đạt/chứng chỉ) - tính trực tiếp từ list đã tải sẵn (course_students/
// student_subject_results/medal_awards), KHÔNG gọi thêm API riêng - dữ liệu 100% thật, không có
// field đếm sẵn nào từ BE cho đúng 3 số này nên tính ở FE.
const StatPill = ({label, value, valueClassName}) => {
    const t = useTranslate()
    return (
        <div style={{background: 'rgba(255,255,255,0.08)'}} className="flex-1 min-w-max px-3 py-2 flex flex-col gap-0.5 rounded-xl">
            <span className="text-2xs text-blue-100 uppercase tracking-wider">{t(label)}</span>
            <span className={classNames("font-bold text-base text-white", valueClassName)}>{value}</span>
        </div>
    )
}

const StudentViewCard = ({item}) => {
    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)
    const {
        avatar,
        user,
        identifier_type,
        identifier_code,
        student_idcode, link_secret_code, user_links = [],
        contact_address,
        student_subject_results = [],
        course_students = [],
        teacher_classes = [],
        is_teacher = false,
        medal_awards = [],
        parish_name,
        diocese_name,
        diocese,
        edit,
        notes,
        patron_saint_name,
        prefix_title,
        fullname,
        contact_phone,
        contact_email,
        date_of_birth,
        date_of_birth_is_estimated,
        place_of_birth,
        gender,
        religious_order,
        religious_order_name,
        identity_status,
        updated_at,
    } = item ?? {}

    const typeIdentify = _.find(IdentifierTypes, {value: identifier_type})?.label
    // BE lưu NULL thật khi học viên chưa có ngày sinh (không bịa giá trị giả) - chỉ cần check rỗng.
    const isUnknownDob = !date_of_birth
    const isIdentityVerified = (identity_status ?? IDENTITY_STATUS.UNVERIFIED) === IDENTITY_STATUS.VERIFIED

    // activeKey điều khiển được (trước đây Tabs không controlled, chỉ track current cho có) - cần
    // controlled để nút "Cập nhật xác minh" ở header nhảy thẳng sang tab Xác minh danh tính.
    const [current, setCurrent] = useState('0')

    const [subjectSearch, setSubjectSearch] = useState('')
    const filteredSubjectResults = useMemo(() => {
        const q = subjectSearch.trim().toLowerCase()
        if (!q) return student_subject_results
        return student_subject_results.filter((r) => (getTitle(r?.subject) || '').toLowerCase().includes(q))
    }, [student_subject_results, subjectSearch])

    const passedSubjectCount = useMemo(
        () => student_subject_results.filter((r) => r?.result_status === 1).length,
        [student_subject_results]
    )

    const [courseTeacherLimit, setTeacherLimit] = useState(2)
    const [courseStudentLimit, setStudentLimit] = useState(2)

    const visibleTeacher = teacher_classes.slice(0, courseTeacherLimit)
    const visibleStudent = course_students.slice(0, courseStudentLimit)

    const hasContactInfo = _.some([
        parish_name, diocese_name, religious_order_name, contact_phone, contact_email, contact_address, user,
    ])

    return (
        <div className="flex flex-col gap-4">
            {/* Header - nền gradient tối, avatar vuông bo góc + badge "Đã XM" đè lên khi đã xác minh
                danh tính (identity_status thật, không bịa), hàng giới tính/ngày sinh, nút "Cập nhật
                xác minh" nhảy sang tab Xác minh danh tính (chỉ hiện với người có quyền edit lớp/tổ
                chức này - cùng flag `edit` dùng cho mọi hành động quản trị khác), và 3 số liệu tóm tắt. */}
            <div
                style={{background: 'linear-gradient(135deg, #16223d 0%, #24406e 100%)'}}
                className="p-4 md:p-5 flex flex-col gap-4 rounded-2xl text-white">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                        <div className="relative flex-shrink-0">
                            <Avatar
                                shape="square"
                                size={isLg ? 84 : 68}
                                src={avatar || getAvatar(user)}
                                icon={<FaUserGraduate size={32} className="text-color-500"/>}
                                style={{borderRadius: 14}}
                                className="background-200"
                            />
                            {isIdentityVerified && (
                                <span
                                    style={{bottom: -8, left: '50%', transform: 'translateX(-50%)'}}
                                    className="absolute px-2 py-0.5 flex items-center gap-1 rounded-full text-2xs font-bold bg-green-500 text-white whitespace-no-wrap">
                                    <RiShieldCheckFill size={10}/>
                                    {t('verified short')}
                                </span>
                            )}
                        </div>
                        <div className="flex flex-col gap-1.5 pb-1">
                            <span className="font-bold text-lg md:text-xl leading-tight break-words">
                                {`${patron_saint_name || prefix_title || ''} ${fullname}`}
                            </span>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-blue-100">
                                {gender !== 0 && (
                                    <span className="flex items-center gap-1">
                                        <PiGenderIntersexBold size={13}/>
                                        {t('gender')}{': '}
                                        <span className="font-bold text-white">{t(getLabelOfGender(gender))}</span>
                                    </span>
                                )}
                                {!isUnknownDob && (
                                    <span className="flex items-center gap-1">
                                        <BsCalendar2Date size={12}/>
                                        {t('date of birth')}{': '}
                                        <span className="font-bold text-white">
                                            {convertStringToDate(date_of_birth).format('DD/MM/YYYY')}
                                        </span>
                                        {date_of_birth_is_estimated && <EstimatedDobTag/>}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    {edit === true && (
                        <button
                            type="button"
                            onClick={() => setCurrent('1')}
                            className="px-3 py-2 flex items-center gap-1.5 rounded-lg text-xs font-bold text-white bg-blue-500 hover:bg-blue-600 border-0 cursor-pointer flex-shrink-0">
                            <HiOutlineShieldCheck size={15}/>
                            {t('update verification')}
                        </button>
                    )}
                </div>
                <div className="flex flex-wrap gap-2">
                    <StatPill label="courses" value={course_students.length}/>
                    <StatPill
                        label="subjects passed"
                        value={`${passedSubjectCount}/${student_subject_results.length}`}
                        valueClassName={student_subject_results.length > 0 ? 'text-green-400' : undefined}
                    />
                    <StatPill label="certification awarded" value={medal_awards.length} valueClassName="text-amber-400"/>
                </div>
            </div>

            <Tabs
                activeKey={current}
                onChange={setCurrent}
                size="small"
                tabBarExtraContent={
                    <Dropdown
                        trigger={['click']}
                        placement="bottomRight"
                        menu={{
                            items: TAB_META.map((m) => ({key: m.key, label: t(m.labelKey)})),
                            selectedKeys: [current],
                            onClick: ({key}) => setCurrent(key),
                        }}>
                        <button
                            type="button"
                            className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-color-50 text-color-300">
                            <FiMoreHorizontal size={16}/>
                        </button>
                    </Dropdown>
                }
                items={[
                    {
                        key: '0',
                        label: t('basic info'),
                        children: (
                            <div className="flex flex-col gap-3">
                                {/* Luôn xếp dọc 1 cột (không dùng grid/md:grid-cols-2) - modal chứa
                                    StudentViewCard cố định hẹp (600-640px, xem useStudentForm.js/
                                    CourseClassStudentTab.js), breakpoint md: theo viewport trình
                                    duyệt (không theo bề rộng modal) nên vẫn bật 2 cột trên màn hình
                                    desktop dù modal chật, làm field trong "Thông tin cá nhân" (vd họ
                                    tên dài) bị xuống dòng khó nhìn (bạn báo). Cha (dòng trên) đã là
                                    flex-col nên các InfoCard dưới đây là sibling trực tiếp, không cần
                                    div bọc riêng nữa. */}
                                <InfoCard title="personal information">
                                    <div className="grid grid-cols-2 gap-3">
                                        <ItemInformationStudent
                                            primary={true}
                                            label="full name"
                                            value={`${getAliasName(item, 'patron_saint') || ''} ${fullname}`}
                                        />
                                        {gender !== 0 && (
                                            <ItemInformationStudent label="gender" value={t(getLabelOfGender(gender))}/>
                                        )}
                                        {!isUnknownDob && (
                                            <ItemInformationStudent
                                                label="date of birth"
                                                value={
                                                    <span className="flex items-center gap-1">
                                                        {convertStringToDate(date_of_birth).format('DD/MM/YYYY')}
                                                        {date_of_birth_is_estimated && <EstimatedDobTag/>}
                                                    </span>
                                                }
                                            />
                                        )}
                                        {place_of_birth && (
                                            <ItemInformationStudent label="place of birth" value={place_of_birth}/>
                                        )}
                                        {student_idcode && (
                                            <ItemInformationStudent label="student code" value={student_idcode}/>
                                        )}
                                        {(edit === true && identifier_code) && (
                                            <ItemInformationStudent label={t(typeIdentify)} value={identifier_code}/>
                                        )}
                                        {link_secret_code && (
                                            <div
                                                onClick={() => { copyToClipboard(link_secret_code); message.info(t('copied')) }}
                                                className="flex flex-col">
                                                <span className="text-xs text-color-400 tracking-wider">
                                                    {t('link code')}
                                                </span>
                                                <span className="font-semibold text-sm text-secondary hover:text-secondary-600 break-words tracking-wide cursor-pointer">
                                                    {link_secret_code}
                                                    <IoCopyOutline size={12} className="ml-1"/>
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </InfoCard>
                                {/* "Tổ/Đội/Nhóm" trong mockup CHƯA có field thật tương ứng ở Student
                                    (đã rà app/Transformers/StudentTransformer.php và
                                    CourseClassStudentTransformerBasic.php - không có khái niệm
                                    tổ/đội/nhóm theo lớp) - KHÔNG hiện, tránh bịa dữ liệu. Xem báo
                                    cáo gửi kèm để bổ sung field thật nếu cần dùng sau này. */}
                                {notes && (
                                    <InfoCard title="notes and history">
                                        <div className="p-2.5 background-50 rounded-lg text-xs text-color-300 italic leading-snug whitespace-pre-line break-words">
                                            {notes}
                                        </div>
                                    </InfoCard>
                                )}
                                {user_links.length > 0 && (
                                    <InfoCard title="link account">
                                        {user_links.map((e,i) => (
                                            <div className="flex items-center gap-2">
                                                <Avatar size={32} src={e?.user?.avatar} className="background-200"/>
                                                <div className="flex flex-col flex-1 min-w-0">
                                                    <Link to={getLinkToDetail(e?.user)} className="font-bold text-color-000 no-underline">
                                                        {getTitle(e?.user)}
                                                    </Link>
                                                </div>
                                            </div>
                                        ))}
                                    </InfoCard>
                                )}
                                {hasContactInfo && (
                                    <InfoCard title="contact information">
                                        <div className="grid grid-cols-2 gap-3">
                                            {renderIf(
                                                _.some([parish_name, diocese_name]),
                                                <ItemInformationStudent
                                                    label="belong to parish"
                                                    value={`${parish_name} ${diocese_name ? ` - ${diocese?.fullname}` : ''}`}
                                                />
                                            )}
                                            {religious_order_name && (
                                                <ItemInformationStudent
                                                    label="belong to religious order"
                                                    value={
                                                        <Link to={getLinkToDetail(religious_order)} className="no-underline">
                                                            {religious_order_name}
                                                        </Link>
                                                    }
                                                />
                                            )}
                                            {contact_phone && (
                                                <ItemInformationStudent label="phone" value={contact_phone}/>
                                            )}
                                            {contact_email && (
                                                <ItemInformationStudent label="email" value={contact_email}/>
                                            )}
                                            {contact_address && (
                                                <ItemInformationStudent label="address" value={contact_address}/>
                                            )}
                                            {user && (
                                                <div className="flex flex-col gap-1">
                                                    <span className="text-xs text-color-400 tracking-wider">
                                                        {t('link account')}
                                                    </span>
                                                    <div className="flex items-center gap-3">
                                                        <Avatar
                                                            size={26}
                                                            src={getAvatar(user)}
                                                            icon={<FaUserTie size={16} className="text-color-500"/>}
                                                            className="flex flex-center background-200"
                                                        />
                                                        {renderIfElse(
                                                            edit === true,
                                                            <Link
                                                                target="_blank" to={getLinkToDetail(user)}
                                                                className="font-medium text-color-000 no-underline leading-snug">
                                                                {getAliasName(user, 'patron_saint')}
                                                                {` ${getTitle(user)}`}
                                                                <HiOutlineLink size={12} className="ml-1 text-primary-600"/>
                                                            </Link>,
                                                            <span className="font-medium text-color-000 leading-snug">
                                                                {getAliasName(user, 'patron_saint')}
                                                                {getTitle(user)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </InfoCard>
                                )}
                            </div>
                        ),
                    },
                    {
                        key: '1',
                        label: t('identity verification'),
                        // onRefresh CHƯA nối được ở đây - StudentViewCard nhận `item` snapshot 1 lần
                        // qua FetchStudentById (useStudentForm.js), không có refreshKey/refetch truyền
                        // xuống tới đây. Nộp giấy tờ vẫn thành công (API call thật), chỉ là banner
                        // trạng thái trong modal không tự cập nhật ngay - phải đóng/mở lại modal mới
                        // thấy trạng thái mới. Việc riêng cần bàn thêm (nối refreshKey qua
                        // useStudentForm.js), không tự ý đổi ở đây.
                        children: <IdentityVerificationSection item={item}/>,
                    },
                    {
                        key: '2',
                        label: t('enrolled courses'),
                        children: renderIfElse(
                            course_students.length > 0,
                            <div className="flex flex-col gap-3">
                                {course_students.map((e, i) => {
                                    const {
                                        course,
                                        registered_classes = [],
                                        medal_awards: courseMedals = [],
                                        result_status,
                                    } = e ?? {};

                                    let label_status;
                                    let color_status;
                                    switch (result_status) {
                                        case 1:
                                            label_status = 'passed';
                                            color_status = 'text-green-600 bg-green-50 border-green-200';
                                            break;
                                        case 0:
                                            label_status = 'failed';
                                            color_status = 'text-red-600 bg-red-50 border-red-200';
                                            break;
                                        default:
                                            label_status = 'not assessed yet';
                                            color_status = 'text-color-300 background-50 border-color-100';
                                            break;
                                    }

                                    // Gộp khoảng thời gian từ TẤT CẢ lớp đã ghi danh của khóa này (có
                                    // thể ghi danh nhiều lớp cùng 1 khóa) - lấy sớm nhất → muộn nhất.
                                    const fromDates = registered_classes.map((c) => c?.from_date).filter(Boolean)
                                    const toDates = registered_classes.map((c) => c?.to_date).filter(Boolean)
                                    const fromDate = fromDates.length ? fromDates.reduce((a, b) => (a < b ? a : b)) : null
                                    const toDate = toDates.length ? toDates.reduce((a, b) => (a > b ? a : b)) : null

                                    return (
                                        <div key={i} className="flex flex-col gap-3 p-3 border border-color-100 rounded-2xl">
                                            <div className="flex items-center justify-between gap-2 pb-2 border-b border-color-50">
                                                <span className="font-bold text-2xs text-slate-400 uppercase tracking-wider">
                                                    {t('enrolled courses')}
                                                </span>
                                                {result_status !== null && (
                                                    <span className={classNames('px-2 py-1 rounded-full text-2xs font-bold uppercase tracking-wide border flex-shrink-0', color_status)}>
                                                        {t('status')}{': '}{t(label_status)}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                <Link
                                                    to={getLinkToDetail(course)}
                                                    target="_blank"
                                                    className="font-bold text-sm text-color-000 no-underline break-words">
                                                    {getTitle(course)}
                                                </Link>
                                                {fromDate && (
                                                    <span className="flex items-center gap-1.5 text-xs text-color-400">
                                                        <BsCalendar2Date size={12} className="flex-shrink-0"/>
                                                        {t('time takes place')}{': '}{renderFromToDate(fromDate, toDate)}
                                                    </span>
                                                )}
                                            </div>
                                            {courseMedals.length > 0 && (
                                                <div className="flex flex-col gap-2">
                                                    <span className="font-bold text-2xs text-slate-400 uppercase tracking-wider">
                                                        {t('certification awarded')}
                                                    </span>
                                                    {/* flex-wrap + min-width (không grid-cols theo
                                                        breakpoint viewport) - card này nằm trong modal
                                                        hẹp hơn hẳn viewport, sm:grid-cols-3 vẫn ép đủ 3
                                                        cột theo bề rộng MÀN HÌNH nên bị chật/dồn (bạn báo
                                                        lỗi) - flex-wrap tự xuống hàng theo đúng bề rộng
                                                        THẬT của modal. */}
                                                    <div className="flex flex-wrap gap-2">
                                                        {courseMedals.map((m, mi) => {
                                                            const {medal, eff_date, ref} = m ?? {}
                                                            return (
                                                                <div key={mi} style={{minWidth: 140}} className="flex-1 p-2.5 flex flex-col gap-1 background-50 rounded-xl">
                                                                    {medal?.avatar ? (
                                                                        <Image
                                                                            src={medal.avatar}
                                                                            style={{width: 32, height: 32}}
                                                                            wrapperClassName="rounded-lg object-cover"
                                                                            className="rounded-lg object-cover"
                                                                            preview={{
                                                                                mask: (
                                                                                    <div className="w-full h-full relative">
                                                                                        <HiViewfinderCircle size={12} className="absolute text-white opacity-85 hover:opacity-100 custom-img-view-icon"/>
                                                                                    </div>
                                                                                ),
                                                                            }}
                                                                        />
                                                                    ) : null}
                                                                    <span className="font-bold text-xs text-red-500 leading-snug break-words">
                                                                        {getTitle(medal)}
                                                                    </span>
                                                                    <span className="text-2xs text-color-400">
                                                                        {formatDate(eff_date, 'HH:mm - DD/MM/YYYY')}
                                                                    </span>
                                                                    {ref && (
                                                                        <span className="text-2xs text-color-300">
                                                                            {t('by')}{': '}
                                                                            <Link
                                                                                to={getLinkToDetail(ref)}
                                                                                target="_blank"
                                                                                className="font-semibold text-indigo-500 no-underline">
                                                                                {getTitle(ref)}
                                                                            </Link>
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>,
                            renderEmptyHolderMember()
                        ),
                    },
                    {
                        key: '3',
                        label: t('subject results'),
                        children: (
                            <div className="flex flex-col gap-3">
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <Input
                                        allowClear
                                        prefix={<IoSearchOutline className="text-color-400"/>}
                                        placeholder={t('search subject placeholder')}
                                        value={subjectSearch}
                                        onChange={(e) => setSubjectSearch(e.target.value)}
                                        className="flex-1 min-w-0"
                                        style={{maxWidth: 320}}
                                    />
                                    <span className="text-xs text-color-400 tracking-wider flex-shrink-0">
                                        {t('total')}{': '}
                                        <span className="font-bold text-color-000">{student_subject_results.length}</span>
                                        {' '}{t('subjects').toLowerCase()}
                                    </span>
                                </div>
                                {renderIfElse(
                                    filteredSubjectResults.length > 0,
                                    <div className="flex flex-col gap-2">
                                        {filteredSubjectResults.map((e, i) => {
                                            const {
                                                subject,
                                                course_class,
                                                result_date,
                                                result_status,
                                                score_type,
                                                score_text,
                                                score_number,
                                            } = e ?? {};

                                            let label_status;
                                            let color_status;
                                            switch (result_status) {
                                                case 1:
                                                    label_status = 'passed';
                                                    color_status = 'text-green-600';
                                                    break;
                                                case 0:
                                                    label_status = 'failed';
                                                    color_status = 'text-red-600';
                                                    break;
                                                default:
                                                    label_status = 'not assessed yet';
                                                    color_status = 'text-color-300';
                                                    break;
                                            }

                                            return (
                                                <div key={i} className="flex flex-col border border-color-50 rounded-lg relative">
                                                    <span className="p-2 font-bold text-sm gray-800">
                                                        {getTitle(subject)}
                                                    </span>
                                                    <div className="p-2 border-t border-color-50 flex flex-col gap-1">
                                                        {renderIf(
                                                            _.some([score_text, score_number > 0]),
                                                            <ItemInformation
                                                                showIcon={true}
                                                                label="score"
                                                                value={renderIfElse(
                                                                    score_type === 'text',
                                                                    score_text,
                                                                    score_number
                                                                )}
                                                                className="text-xs tracking-wider italic"
                                                            />
                                                        )}
                                                        <ItemInformation
                                                            showIcon={true}
                                                            label="result date"
                                                            value={formatDate(result_date, 'DD/MM/YYYY')}
                                                            className="text-xs tracking-wider italic"
                                                        />
                                                        <div className="space-x-1 text-xs text-color-400 tracking-wider italic">
                                                            <GoDotFill size={10} className="text-color-400"/>
                                                            <span>{t('learn in the class')}</span>
                                                            <span className="font-semibold text-color-100">
                                                                {getTitle(course_class)}
                                                            </span>
                                                            <span>{t('in the course')}</span>
                                                            <span className="font-semibold text-slate-600">
                                                                {getTitle(course_class?.course)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {renderIf(
                                                        result_status !== null,
                                                        <span
                                                            style={{position: 'absolute', top: 8, right: 8}}
                                                            className={classNames(
                                                                'font-bold text-2xs tracking-wide uppercase',
                                                                color_status
                                                            )}>
                                                            {t(label_status)}
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>,
                                    <EmptyHolder/>
                                )}
                            </div>
                        ),
                    },
                    {
                        key: '4',
                        label: t('activity history'),
                        children: renderIfElse(
                            course_students.length > 0 || is_teacher,
                            <div className="flex flex-col gap-4">
                                {course_students.length > 0 && (
                                    <div className="flex flex-col gap-2">
                                        <div className="flex items-center gap-1.5">
                                            <FaUserGraduate size={12} className="text-indigo-400"/>
                                            <span className="font-bold text-2xs text-slate-400 uppercase tracking-wider">
                                                {t('student')}
                                            </span>
                                        </div>
                                        {visibleStudent.map((e, i) => {
                                            const {course, registered_classes = []} = e ?? {};
                                            return (
                                                <div key={i} className="p-3 flex items-start gap-2.5 border border-color-50 rounded-xl">
                                                    <div className="w-9 h-9 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center flex-shrink-0">
                                                        <PiBookOpenText size={16} className="text-indigo-500"/>
                                                    </div>
                                                    <div className="flex flex-col gap-1.5 min-w-0 flex-1">
                                                        <Link
                                                            to={getLinkToDetail(course)}
                                                            target="_blank"
                                                            className="font-bold text-sm text-color-000 no-underline break-words">
                                                            {getTitle(course)}
                                                        </Link>
                                                        {renderIf(
                                                            registered_classes.length > 0,
                                                            <div className="flex flex-col gap-1.5">
                                                                {registered_classes.map((c, ci) => (
                                                                    <div key={ci} className="flex flex-col gap-0.5 pl-2.5 border-l-2 border-indigo-100">
                                                                        <div className="text-xs text-color-300 break-words">
                                                                            {t('learn in the class')}{': '}
                                                                            <Link
                                                                                to={getLinkToDetail(c)}
                                                                                target="_blank"
                                                                                className="font-semibold text-indigo-500 no-underline">
                                                                                {getTitle(c)}
                                                                            </Link>
                                                                        </div>
                                                                        {renderIf(
                                                                            c?.from_date,
                                                                            <div className="flex items-center gap-1 text-2xs text-color-300">
                                                                                <BsCalendar2Date size={11} className="text-color-300 flex-shrink-0"/>
                                                                                <span>{renderFromToDate(c?.from_date, c?.to_date)}</span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {course_students.length > courseStudentLimit && (
                                            <div className="mt-2 flex justify-end">
                                                <button
                                                    type="button"
                                                    onClick={() => setStudentLimit(prev => prev + 2)}
                                                    className="text-color-400 hover:text-secondary-500 tracking-wider">
                                                    {t('see more')}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {is_teacher && teacher_classes.length > 0 && (
                                    <div className="flex flex-col gap-2">
                                        <div className="flex items-center gap-1.5">
                                            <FaUserTie size={12} className="text-amber-500"/>
                                            <span className="font-bold text-2xs text-slate-400 uppercase tracking-wider">
                                                {t('teacher')}
                                            </span>
                                        </div>
                                        {visibleTeacher.map((courseClass, i) => (
                                            <div key={i} className="p-3 flex items-start gap-2.5 border border-color-50 rounded-xl">
                                                <div className="w-9 h-9 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center flex-shrink-0">
                                                    <PiBookOpenText size={16} className="text-amber-500"/>
                                                </div>
                                                <div className="flex flex-col gap-1.5 min-w-0 flex-1">
                                                    <Link
                                                        to={getLinkToDetail(courseClass?.course)}
                                                        target="_blank"
                                                        className="font-bold text-sm text-color-000 no-underline break-words">
                                                        {getTitle(courseClass?.course)}
                                                    </Link>
                                                    <div className="flex flex-col gap-0.5 pl-2.5 border-l-2 border-amber-100">
                                                        <div className="text-xs text-color-300 break-words">
                                                            {t('teaching the class')}{': '}
                                                            <Link
                                                                to={getLinkToDetail(courseClass)}
                                                                target="_blank"
                                                                className="font-semibold text-amber-600 no-underline">
                                                                {getTitle(courseClass)}
                                                            </Link>
                                                        </div>
                                                        {renderIf(
                                                            courseClass?.from_date,
                                                            <div className="flex items-center gap-1 text-2xs text-color-300">
                                                                <BsCalendar2Date size={11} className="text-color-300 flex-shrink-0"/>
                                                                <span>{renderFromToDate(courseClass?.from_date, courseClass?.to_date)}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        {teacher_classes.length > courseTeacherLimit && (
                                            <div className="mt-2 flex justify-end">
                                                <button
                                                    type="button"
                                                    onClick={() => setTeacherLimit(prev => prev + 2)}
                                                    className="text-color-400 hover:text-secondary-500 tracking-wider">
                                                    {t('see more')}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>,
                            renderEmptyHolderMember()
                        ),
                    },
                ]}
            />
            {/* "Lần cập nhật cuối" - dùng field updated_at (mới thêm ở StudentTransformer.php, trước
                đây transformer chỉ trả created_at) - additive, không đổi field/hành vi nào khác. */}
            {updated_at && (
                <div className="pt-2 border-t border-color-50 text-2xs text-color-300 tracking-wider text-right">
                    {t('last updated')}{': '}{formatDate(updated_at, 'HH:mm - DD/MM/YYYY')}
                </div>
            )}
        </div>
    )
}

export default StudentViewCard
